import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PageLayout from '../components/layout/PageLayout';
import VideoSidebar from '../components/training/VideoSidebar';
import InlineVideoPlayer from '../components/training/InlineVideoPlayer';
import VideoDetailPanel from '../components/training/VideoDetailPanel';
import { useTrainingVideos } from '../hooks/useTrainingVideos';
import { useTranslate } from '../hooks/useTranslate';
import { TRAINING_MESSAGES } from '../constants/training';
import type { TrainingVideo } from '../types/training';

const TrainingPage: React.FC = () => {
    const { t } = useTranslate();
    const {
        filteredVideos,
        categories,
        activeCategory,
        searchQuery,
        loading,
        error,
        setCategory,
        setSearchQuery,
    } = useTrainingVideos();

    const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
    const [videoMeta, setVideoMeta] = useState<TrainingVideo | null>(null);

    // Auto-select first video when videos load (or when filter changes)
    useEffect(() => {
        if (filteredVideos.length > 0 && !selectedVideoId) {
            setSelectedVideoId(filteredVideos[0].id);
        }
    }, [filteredVideos, selectedVideoId]);

    // Clear selection if filtered list no longer contains the selected video
    useEffect(() => {
        if (selectedVideoId && filteredVideos.length > 0 && !filteredVideos.some(v => v.id === selectedVideoId)) {
            setSelectedVideoId(filteredVideos[0].id);
            setVideoMeta(null);
        }
    }, [filteredVideos, selectedVideoId]);

    const handleSelectVideo = useCallback((id: string) => {
        setSelectedVideoId(id);
        setVideoMeta(null);
    }, []);

    const handleVideoMetaLoaded = useCallback((meta: TrainingVideo) => {
        setVideoMeta(meta);
    }, []);

    // Previous / Next navigation
    const currentIndex = selectedVideoId ? filteredVideos.findIndex(v => v.id === selectedVideoId) : -1;

    const handlePrevious = useMemo(() => {
        if (currentIndex > 0) {
            const prevId = filteredVideos[currentIndex - 1].id;
            return () => handleSelectVideo(prevId);
        }
        return null;
    }, [currentIndex, filteredVideos, handleSelectVideo]);

    const handleNext = useMemo(() => {
        if (currentIndex >= 0 && currentIndex < filteredVideos.length - 1) {
            const nextId = filteredVideos[currentIndex + 1].id;
            return () => handleSelectVideo(nextId);
        }
        return null;
    }, [currentIndex, filteredVideos, handleSelectVideo]);

    // Full-page loading state
    if (loading) {
        return (
            <PageLayout>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center space-y-4">
                        <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#D31145] rounded-full animate-spin" />
                        <p className="text-gray-400 text-sm">{t(TRAINING_MESSAGES.LOADING_VIDEOS)}</p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    // Full-page error state
    if (error) {
        return (
            <PageLayout>
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-red-500 text-sm">{t(TRAINING_MESSAGES.FAILED_TO_LOAD)}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            {/* 3-column learning portal layout */}
            <div className="flex flex-col xl:flex-row flex-1 min-h-0 overflow-hidden">

                {/* ── Left: Video Sidebar ── */}
                <div className="xl:w-80 xl:flex-shrink-0 order-3 xl:order-1 border-t xl:border-t-0">
                    <div className="xl:h-full h-80 md:h-96">
                        <VideoSidebar
                            videos={filteredVideos}
                            categories={categories}
                            activeCategory={activeCategory}
                            searchQuery={searchQuery}
                            selectedVideoId={selectedVideoId}
                            onSelectVideo={handleSelectVideo}
                            onSearchChange={setSearchQuery}
                            onCategoryChange={setCategory}
                        />
                    </div>
                </div>

                {/* ── Center: Video Player ── */}
                <div className="flex-1 order-1 xl:order-2 flex flex-col min-w-0 bg-gray-950">
                    <div className="flex-1 flex items-center justify-center p-4 md:p-6 xl:p-8">
                        <div className="w-full max-w-4xl">
                            <InlineVideoPlayer
                                videoId={selectedVideoId}
                                onVideoMetaLoaded={handleVideoMetaLoaded}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Right: Details Panel ── */}
                <div className="xl:w-[300px] xl:flex-shrink-0 order-2 xl:order-3 border-t xl:border-t-0">
                    <div className="xl:h-full">
                        <VideoDetailPanel
                            video={videoMeta}
                            onPrevious={handlePrevious}
                            onNext={handleNext}
                        />
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default TrainingPage;
